import React, { useState, useEffect, useRef } from 'react';
import { authFetch } from '../services/api';
import './ApplicationDependencies.css';

interface ResourceAllocationRecord {
  allocation_id: number;
  application_id_code: string;
  application_name?: string;
  phase_name: string;
  resource_pool_id: number | null;
  allocated_fte: number;
  integration_group_id?: string;
  grouping_id?: string;
  group_name?: string;
  created_at?: string;
  updated_at?: string;
}

interface ScheduleRecord {
  application_id: string;
  integration_group_id: string;
  schedule_status: string;
  deployment_status?: string;
}

interface ResourcePool {
  resource_pool_id: number;
  resource_type: string;
  available_capacity_percent: number;
  resource_location: 'On-Site' | 'Off-Shore';
}

interface Application {
  application_id: string;
  application_id_code?: string;
  application_name?: string;
  description?: string;
  disposition?: string;
  integration_group_id?: string;
}

interface IntegrationGroup {
  integration_group_id: string;
  integration_group_description: string;
  application_count: number;
}

// Helper function for disposition colors
const getDispositionColor = (disposition: string | undefined) => {
  switch (disposition) {
    case 'Retire': return { bg: '#ffebee', color: '#c62828' };
    case 'Replace': return { bg: '#fff3e0', color: '#e65100' };
    case 'Re-Platform': return { bg: '#e3f2fd', color: '#1565c0' };
    case 'Retain': return { bg: '#e8f5e9', color: '#2e7d32' };
    default: return { bg: '#f5f5f5', color: '#616161' };
  }
};

const ResourceAllocation: React.FC = () => {
  // Standard phases used across the application
  const STANDARD_PHASES = [
    'Assessment_Planning',
    'Design_Architecture',
    'Migration_Execution',
    'Testing_Validation',
    'GoLive_Optimization'
  ];

  const [records, setRecords] = useState<ResourceAllocationRecord[]>([]);
  const [resourcePools, setResourcePools] = useState<ResourcePool[]>([]);
  const [applications, setApplications] = useState<Application[]>([]);
  const [singleFormApplications, setSingleFormApplications] = useState<Application[]>([]);
  const [integrationGroups, setIntegrationGroups] = useState<IntegrationGroup[]>([]);
  const [scheduleData, setScheduleData] = useState<ScheduleRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showAppDropdown, setShowAppDropdown] = useState(false);
  const [showSingleAppDropdown, setShowSingleAppDropdown] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [previewData, setPreviewData] = useState<any[]>([]);
  const [filterApplicationCode, setFilterApplicationCode] = useState<string>('');
  const [filterPhase, setFilterPhase] = useState<string>('');
  const [filterGroupingId, setFilterGroupingId] = useState<string>('');
  const [filterResourcePool, setFilterResourcePool] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('need_approval');
  const [editingRecord, setEditingRecord] = useState<ResourceAllocationRecord | null>(null);
  const [viewOnlyMode, setViewOnlyMode] = useState(false);
  const [formData, setFormData] = useState({
    groupingId: '',
    applicationIdCode: '',
    phaseName: '',
    resourcePoolId: '',
    allocatedFte: ''
  });

  // New enhanced form data for bulk phase allocation
  const [bulkFormData, setBulkFormData] = useState({
    groupingId: '',
    applicationIdCode: '',
    resourcePoolId: '',
    allocations: {} as Record<string, string> // phase -> FTE mapping
  });

  const [showBulkForm, setShowBulkForm] = useState(false);

  useEffect(() => {
    fetchRecords();
    fetchResourcePools();
    fetchIntegrationGroups();
    fetchScheduleData();
  }, []);

  // Load applications when grouping ID changes in bulk form
  useEffect(() => {
    if (bulkFormData.groupingId) {
      fetchApplicationsForGroup(bulkFormData.groupingId);
    } else {
      setApplications([]);
    }
  }, [bulkFormData.groupingId]);

  // Load applications when grouping ID changes in single form
  useEffect(() => {
    if (formData.groupingId) {
      fetchApplicationsForSingleForm(formData.groupingId);
    } else {
      setSingleFormApplications([]);
    }
  }, [formData.groupingId]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.custom-dropdown')) {
        setShowAppDropdown(false);
        setShowSingleAppDropdown(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const fetchRecords = async () => {
    try {
      const response = await authFetch('/api/resource-allocation');
      // Don't treat 404 as an error - it just means no data
      if (!response.ok && response.status !== 404) {
        throw new Error('Failed to fetch');
      }
      const data = await response.json();
      if (Array.isArray(data)) {
        // Sort by Allocation ID in ascending order
        data.sort((a: ResourceAllocationRecord, b: ResourceAllocationRecord) => a.allocation_id - b.allocation_id);
        setRecords(data);
      } else {
        // API returned non-array (empty or error), treat as empty
        setRecords([]);
      }
      setLoading(false);
    } catch (err: any) {
      // Only set error for actual failures, not empty data
      console.error('Error fetching resource allocation records:', err);
      setRecords([]);
      setLoading(false);
    }
  };

  const fetchResourcePools = async () => {
    try {
      const response = await authFetch('/api/resource-capacity');
      if (!response.ok) throw new Error('Failed to fetch resource pools');
      const data = await response.json();
      setResourcePools(data);
    } catch (err: any) {
    }
  };

  const fetchApplicationsForGroup = async (groupId: string) => {
    try {
      const response = await authFetch(`/api/application-dependencies/lookup/applications/${groupId}`);
      const result = await response.json();
      if (result.success) {
        setApplications(result.data || []);
      } else {
        setApplications([]);
      }
    } catch (err: any) {
      console.error('Error loading applications:', err);
      setApplications([]);
    }
  };

  const fetchApplicationsForSingleForm = async (groupId: string) => {
    try {
      const response = await authFetch(`/api/application-dependencies/lookup/applications/${groupId}`);
      const result = await response.json();
      if (result.success) {
        setSingleFormApplications(result.data || []);
      } else {
        setSingleFormApplications([]);
      }
    } catch (err: any) {
      console.error('Error loading applications:', err);
      setSingleFormApplications([]);
    }
  };

  const fetchIntegrationGroups = async () => {
    try {
      const response = await authFetch('/api/application-dependencies/lookup/integration-groups');
      const result = await response.json();
      if (result.success) {
        setIntegrationGroups(result.data || []);
      } else {
        setIntegrationGroups([]);
      }
    } catch (err: any) {
      console.error('Error loading integration groups:', err);
      setIntegrationGroups([]);
    }
  };

  const fetchScheduleData = async () => {
    try {
      const response = await authFetch('/api/roadmap-schedule');
      const result = await response.json();
      if (result.success && Array.isArray(result.data)) {
        setScheduleData(result.data);
      } else {
        setScheduleData([]);
      }
    } catch (err: any) {
      console.error('Error loading schedule data:', err);
      setScheduleData([]);
    }
  };

  // Check if a record is read-only based on deployment status
  // Records are read-only if their application is In Development, Deployed, or On Hold
  const isRecordReadOnly = (record: ResourceAllocationRecord): boolean => {
    const readOnlyStatuses = ['In Development', 'Deployed', 'On Hold'];

    // Check by application ID - use deployment_status as source of truth
    const appSchedule = scheduleData.find(s => s.application_id === record.application_id_code);
    if (appSchedule && appSchedule.deployment_status && readOnlyStatuses.includes(appSchedule.deployment_status)) {
      return true;
    }

    return false;
  };

  // Get the status for display - use deployment_status as source of truth
  const getRecordStatus = (record: ResourceAllocationRecord): string | null => {
    const appSchedule = scheduleData.find(s => s.application_id === record.application_id_code);
    if (appSchedule && appSchedule.deployment_status) {
      return appSchedule.deployment_status;
    }
    return null;
  };

  // Check if an application can be added (only applications not yet in development/deployed)
  const isApplicationEditable = (applicationId: string): boolean => {
    const appSchedule = scheduleData.find(s => s.application_id === applicationId);
    // If no schedule exists or no deployment status, it's editable
    if (!appSchedule || !appSchedule.deployment_status) return true;
    // Only editable if not In Development, Deployed, or On Hold
    const readOnlyStatuses = ['In Development', 'Deployed', 'On Hold'];
    return !readOnlyStatuses.includes(appSchedule.deployment_status);
  };

  // Filter applications to only show editable ones in the add forms
  const editableApplications = applications.filter(app => isApplicationEditable(app.application_id));
  const editableSingleFormApplications = singleFormApplications.filter(app => isApplicationEditable(app.application_id));

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadFile(file);
    setShowUploadModal(true);
    
    const reader = new FileReader();
    reader.onload = async (event) => {
      const text = event.target?.result as string;
      const lines = text.split('\n').filter(line => line.trim());
      const headers = lines[0].split(',').map(h => h.trim());
      
      const data = [];
      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(',').map(v => v.trim());
        const row: any = {};
        headers.forEach((header, index) => {
          row[header] = values[index] || '';
        });
        data.push(row);
      }
      
      setPreviewData(data);
    };
    
    reader.readAsText(file);
  };

  const handleBulkImport = async () => {
    try {
      const response = await authFetch('/api/resource-allocation/bulk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(previewData)
      });
      
      const data = await response.json();
      
      if (data.imported > 0) {
        setSuccess(`Successfully imported ${data.imported} resource allocation records`);
        fetchRecords();
      }
      
      if (data.errors && data.errors.length > 0) {
        const errorMessage = data.errors.join('\n');
        setError(`Import completed with errors:\n${errorMessage}`);
      }
      
      setShowUploadModal(false);
      setPreviewData([]);
      setUploadFile(null);
    } catch (err: any) {
      setError(err.message || 'Failed to import data');
    }

    // Clear file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const downloadTemplate = () => {
    const template = `Grouping ID,Application ID/Code,Phase Name,Resource Pool ID,Allocated FTE
IG001,APP001,Assessment_Planning,1001,0.5
IG001,APP001,Design_Architecture,1002,1.0
IG002,APP002,Migration_Execution,1003,2.5
IG002,APP003,Testing_Validation,1004,1.5
IG003,APP004,GoLive_Optimization,1005,0.75

Available Phase Names: ${STANDARD_PHASES.join(', ')}`;

    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'resource_allocation_template.csv';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const downloadData = () => {
    if (records.length === 0) {
      alert('No data to download');
      return;
    }

    // Match template format: Grouping ID,Application ID/Code,Phase Name,Resource Pool ID,Allocated FTE
    const headers = ['Grouping ID', 'Application ID/Code', 'Phase Name', 'Resource Pool ID', 'Allocated FTE'];
    const csvRows = [headers.join(',')];

    records.forEach(record => {
      const row = [
        `"${(record.integration_group_id || record.grouping_id || '').replace(/"/g, '""')}"`,
        `"${(record.application_id_code || '').replace(/"/g, '""')}"`,
        `"${(record.phase_name || '').replace(/"/g, '""')}"`,
        record.resource_pool_id || '',
        record.allocated_fte || 0
      ];
      csvRows.push(row.join(','));
    });

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `resource_allocation_data_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload = {
        integration_group_id: formData.groupingId || null,
        application_id_code: formData.applicationIdCode,
        phase_name: formData.phaseName,
        resource_pool_id: formData.resourcePoolId ? parseInt(formData.resourcePoolId) : null,
        allocated_fte: formData.allocatedFte ? parseFloat(formData.allocatedFte) : 0
      };

      if (editingRecord) {
        const response = await authFetch(`/api/resource-allocation/${editingRecord.allocation_id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to update');
        }
        setSuccess('Resource allocation record updated successfully');
      } else {
        const response = await authFetch('/api/resource-allocation', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to create');
        }
        setSuccess('Resource allocation record created successfully');
      }

      fetchRecords();
      
      // Clear any error messages
      setError('');
      
      // If editing, close the form and navigate back to main screen
      if (editingRecord) {
        setShowForm(false);
        setEditingRecord(null);
        resetFormData();
      } else {
        // For new records, just clear form but keep modal open
        resetFormData();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save resource allocation record');
    }
  };

  const resetFormData = () => {
    setFormData({
      groupingId: '',
      applicationIdCode: '',
      phaseName: '',
      resourcePoolId: '',
      allocatedFte: ''
    });
  };

  const resetBulkFormData = () => {
    setBulkFormData({
      groupingId: '',
      applicationIdCode: '',
      resourcePoolId: '',
      allocations: {}
    });
  };

  const handleEdit = (record: ResourceAllocationRecord) => {
    const readOnly = isRecordReadOnly(record);
    setEditingRecord(record);
    setViewOnlyMode(readOnly);
    setFormData({
      groupingId: record.grouping_id || '',
      applicationIdCode: record.application_id_code,
      phaseName: record.phase_name,
      resourcePoolId: record.resource_pool_id?.toString() || '',
      allocatedFte: record.allocated_fte?.toString() || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this resource allocation record?')) {
      try {
        const response = await authFetch(`/api/resource-allocation/${id}`, {
          method: 'DELETE'
        });
        if (!response.ok) throw new Error('Failed to delete');
        setSuccess('Resource allocation record deleted successfully');
        fetchRecords();
      } catch (err: any) {
        setError(err.message || 'Failed to delete resource allocation record');
      }
    }
  };

  const handleAddNew = () => {
    setEditingRecord(null);
    resetFormData();
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setShowBulkForm(false);
    setEditingRecord(null);
    setViewOnlyMode(false);
    resetFormData();
    resetBulkFormData();
    setError('');
  };


  const getResourcePoolInfo = (poolId: number | null) => {
    if (!poolId) return { type: 'N/A', capacity: 'N/A', location: 'N/A' };
    const pool = resourcePools.find(p => p.resource_pool_id === poolId);
    return pool ? {
      type: pool.resource_type,
      capacity: `${pool.available_capacity_percent}%`,
      location: pool.resource_location
    } : { type: 'Unknown', capacity: 'N/A', location: 'N/A' };
  };

  // Enhanced bulk submit function for all phases
  const handleBulkSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    // Validate required fields
    if (!bulkFormData.groupingId || !bulkFormData.applicationIdCode || !bulkFormData.resourcePoolId) {
      setError('Grouping ID, Application ID and Resource Pool are required');
      return;
    }

    // Filter out phases with valid FTE values (including 0)
    const validAllocations = Object.entries(bulkFormData.allocations)
      .filter(([_, fte]) => fte !== '' && !isNaN(parseFloat(fte)) && parseFloat(fte) >= 0)
      .map(([phase, fte]) => ({
        integration_group_id: bulkFormData.groupingId,
        application_id_code: bulkFormData.applicationIdCode,
        phase_name: phase,
        resource_pool_id: parseInt(bulkFormData.resourcePoolId),
        allocated_fte: parseFloat(fte)
      }));

    if (validAllocations.length === 0) {
      setError('Please enter valid FTE values (including 0) for at least one phase');
      return;
    }

    try {
      // Create multiple records
      for (const allocation of validAllocations) {
        const response = await authFetch('/api/resource-allocation', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(allocation)
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(`Failed to create ${allocation.phase_name}: ${errorData.error}`);
        }
      }

      setSuccess(`Successfully created ${validAllocations.length} resource allocation records`);
      fetchRecords();
      resetBulkFormData();
      setError('');
    } catch (err: any) {
      setError(err.message || 'Failed to save resource allocations');
    }
  };

  // Build a map of application IDs to their deployment status (used for status filtering)
  const lockedApplications = React.useMemo(() => {
    const statusMap = new Map<string, string>();
    scheduleData.forEach(schedule => {
      if (schedule.deployment_status) {
        statusMap.set(schedule.application_id, schedule.deployment_status);
      }
    });
    return statusMap;
  }, [scheduleData]);

  const filteredRecords = records.filter(record => {
    const appMatch = filterApplicationCode === '' ||
      record.application_id_code.toLowerCase().includes(filterApplicationCode.toLowerCase()) ||
      record.application_name?.toLowerCase().includes(filterApplicationCode.toLowerCase());
    const phaseMatch = filterPhase === '' || record.phase_name === filterPhase;
    const groupingMatch = filterGroupingId === '' ||
      record.grouping_id?.toLowerCase().includes(filterGroupingId.toLowerCase()) ||
      record.group_name?.toLowerCase().includes(filterGroupingId.toLowerCase());
    const resourcePoolMatch = filterResourcePool === '' ||
      record.resource_pool_id?.toString() === filterResourcePool ||
      resourcePools.find(p => p.resource_pool_id === record.resource_pool_id)?.resource_type.toLowerCase().includes(filterResourcePool.toLowerCase());

    // Status filter logic
    const appStatus = lockedApplications.get(record.application_id_code);
    let statusMatch = true;
    if (filterStatus === 'need_approval') {
      // Show applications that don't have a status OR have "Need Approval" status
      statusMatch = !appStatus || appStatus === 'Need Approval';
    } else if (filterStatus === 'approved') {
      statusMatch = appStatus === 'Approved';
    } else if (filterStatus === 'in_development') {
      statusMatch = appStatus === 'In Development';
    } else if (filterStatus === 'deployed') {
      statusMatch = appStatus === 'Deployed';
    } else if (filterStatus === 'on_hold') {
      statusMatch = appStatus === 'On Hold';
    }
    // 'all' shows everything

    return appMatch && phaseMatch && groupingMatch && resourcePoolMatch && statusMatch;
  });

  return (
    <div className="page" style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
      {/* Header - Fixed/Sticky */}
      <div className="page-header" style={{ flexShrink: 0 }}>
        <div className="page-header__content">
          <div className="page-header__title-section">
            <h1 className="page-header__title">Resource Allocation Planning</h1>
            <p className="page-header__subtitle">
              Manage resource allocation for application migration phases
            </p>
          </div>
          <div className="page-header__actions">
            <button
              className="btn btn-primary"
              onClick={() => setShowBulkForm(true)}
              title="Allocate one resource pool across all 5 phases"
            >
              ➕ Add Resource Allocation
            </button>
            <button
              className="btn btn-outline-secondary"
              onClick={handleAddNew}
              title="Create allocation for one phase only"
            >
              📝 Single Phase Only
            </button>
            <button
              className="btn btn-outline-info"
              onClick={downloadTemplate}
              title="Download CSV template for bulk import"
            >
              📋 Download Template
            </button>
            <button
              className="btn btn-outline-primary"
              onClick={() => {
                if (fileInputRef.current) {
                  fileInputRef.current.value = '';
                }
                fileInputRef.current?.click();
              }}
              title="Import allocations from CSV"
            >
              📤 Upload File
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
            />
            {records.length > 0 && (
              <button
                className="btn btn-outline-success"
                onClick={downloadData}
                title="Download current data as CSV"
                style={{ border: '2px solid #28a745' }}
              >
                📥 Download Data
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Scrollable Content Area */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px 16px 16px' }}>

      {loading && (
        <div className="md-loading" style={{ padding: '40px', textAlign: 'center' }}>
          Loading resource allocation records...
        </div>
      )}

      {!loading && (
        <>

      {/* Filter Section - Sticky */}
      {records.length > 0 && (
        <div className="md-card" style={{ marginBottom: '16px', padding: '16px', position: 'sticky', top: 0, zIndex: 100, backgroundColor: 'white', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
          {/* Row 1: Application, Grouping ID, Phase, Resource Pool */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
            <label style={{ fontSize: '14px', fontWeight: '500', minWidth: '120px' }}>Application:</label>
            <input
              type="text"
              value={filterApplicationCode}
              onChange={(e) => setFilterApplicationCode(e.target.value)}
              placeholder="Enter application code..."
              style={{
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                minWidth: '150px'
              }}
            />
            <label style={{ fontSize: '14px', fontWeight: '500', minWidth: '100px' }}>Grouping ID:</label>
            <input
              type="text"
              value={filterGroupingId}
              onChange={(e) => setFilterGroupingId(e.target.value)}
              placeholder="Enter grouping ID..."
              style={{
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                minWidth: '150px'
              }}
            />
            <label style={{ fontSize: '14px', fontWeight: '500', minWidth: '80px' }}>Phase:</label>
            <select
              value={filterPhase}
              onChange={(e) => setFilterPhase(e.target.value)}
              style={{
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                minWidth: '150px'
              }}
            >
              <option value="">All Phases</option>
              {STANDARD_PHASES.map(phase => (
                <option key={phase} value={phase}>
                  {phase.replace(/_/g, ' ')}
                </option>
              ))}
            </select>
            <label style={{ fontSize: '14px', fontWeight: '500', minWidth: '120px' }}>Resource Pool:</label>
            <select
              value={filterResourcePool}
              onChange={(e) => setFilterResourcePool(e.target.value)}
              style={{
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                minWidth: '150px'
              }}
            >
              <option value="">All Resource Pools</option>
              {resourcePools.map(pool => (
                <option key={pool.resource_pool_id} value={pool.resource_pool_id}>
                  [{pool.resource_pool_id}] {pool.resource_type}
                </option>
              ))}
            </select>
          </div>

          {/* Row 2: Status, Clear Filters, Count */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <label style={{ fontSize: '14px', fontWeight: '500', minWidth: '120px' }}>Status:</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              style={{
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                minWidth: '180px',
                backgroundColor: 'white',
                cursor: 'pointer'
              }}
            >
              <option value="need_approval">Need Approval</option>
              <option value="approved">Approved</option>
              <option value="in_development">In Development</option>
              <option value="deployed">Deployed</option>
              <option value="on_hold">On Hold</option>
              <option value="all">All Statuses</option>
            </select>
            {(filterApplicationCode || filterPhase || filterGroupingId || filterResourcePool || filterStatus !== 'need_approval') && (
              <button
                onClick={() => {
                  setFilterApplicationCode('');
                  setFilterPhase('');
                  setFilterGroupingId('');
                  setFilterResourcePool('');
                  setFilterStatus('need_approval');
                }}
                style={{
                  padding: '8px 12px',
                  backgroundColor: '#f5f5f5',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px'
                }}
              >
                Clear Filters
              </button>
            )}
            <span style={{ fontSize: '12px', color: '#666', marginLeft: 'auto' }}>
              Showing {filteredRecords.length} of {records.length} allocations
            </span>
          </div>
        </div>
      )}

      {/* Main Content Card */}
      <div className="md-card" style={{ flex: '1', overflow: 'visible', minHeight: 'auto' }}>
        {records.length === 0 ? (
          <div className="md-empty-state" style={{ padding: '40px' }}>
            <div className="md-empty-icon">📊</div>
            <h3>No Resource Allocation Records Found</h3>
            <p>Use the action cards above to add resource allocations or upload data from a CSV file</p>
          </div>
        ) : (
          <div className="md-table-container" style={{ overflowY: 'visible', height: 'auto', maxHeight: 'none' }}>
            <table className="md-table" style={{ fontSize: '14px' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '8px', fontSize: '14px', fontWeight: '600', whiteSpace: 'nowrap' }}>Grouping ID</th>
                  <th style={{ textAlign: 'left', padding: '8px', fontSize: '14px', fontWeight: '600', whiteSpace: 'nowrap' }}>Application</th>
                  <th style={{ textAlign: 'center', padding: '8px', fontSize: '14px', fontWeight: '600', whiteSpace: 'nowrap' }}>Phase</th>
                  <th style={{ textAlign: 'center', padding: '8px', fontSize: '14px', fontWeight: '600', whiteSpace: 'nowrap' }}>Resource Pool</th>
                  <th style={{ textAlign: 'center', padding: '8px', fontSize: '14px', fontWeight: '600', whiteSpace: 'nowrap' }}>Location</th>
                  <th style={{ textAlign: 'center', padding: '8px', fontSize: '14px', fontWeight: '600', whiteSpace: 'nowrap' }}>FTE</th>
                  <th style={{ textAlign: 'center', padding: '8px', fontSize: '14px', fontWeight: '600', whiteSpace: 'nowrap' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRecords.map(record => {
                  const poolInfo = getResourcePoolInfo(record.resource_pool_id);
                  const readOnly = isRecordReadOnly(record);
                  const status = getRecordStatus(record);
                  return (
                    <tr key={record.allocation_id} style={{ backgroundColor: readOnly ? '#fafafa' : 'white' }}>
                      <td style={{ textAlign: 'left', padding: '8px', fontSize: '14px', whiteSpace: 'nowrap' }}>
                        <div>
                          <div style={{ fontWeight: '500', textAlign: 'left' }}>{record.grouping_id || '-'}</div>
                          {record.group_name && (
                            <div style={{ fontSize: '12px', color: '#666', textAlign: 'left' }}>{record.group_name}</div>
                          )}
                        </div>
                      </td>
                      <td style={{ textAlign: 'left', padding: '8px', fontSize: '14px', whiteSpace: 'nowrap' }}>
                        <div>
                          <div style={{ fontWeight: '500', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {record.application_id_code}
                            <span style={{
                              fontSize: '14px',
                              padding: '4px 12px',
                              borderRadius: '12px',
                              backgroundColor: status === 'Deployed' ? '#e8f5e9' :
                                             status === 'In Development' ? '#e3f2fd' :
                                             status === 'Approved' ? '#c8e6c9' :
                                             status === 'On Hold' ? '#fff3e0' : '#f5f5f5',
                              color: status === 'Deployed' ? '#2e7d32' :
                                    status === 'In Development' ? '#1565c0' :
                                    status === 'Approved' ? '#1b5e20' :
                                    status === 'On Hold' ? '#e65100' : '#757575',
                              fontWeight: '600'
                            }}>
                              {status || 'Need Approval'}
                            </span>
                          </div>
                          {record.application_name && (
                            <div style={{ fontSize: '12px', color: '#666', textAlign: 'left' }}>{record.application_name}</div>
                          )}
                        </div>
                      </td>
                      <td style={{ textAlign: 'center', padding: '8px', fontSize: '14px', whiteSpace: 'nowrap' }}>
                        {record.phase_name}
                      </td>
                      <td style={{ textAlign: 'center', padding: '8px', fontSize: '14px', whiteSpace: 'nowrap' }}>
                        <div style={{ fontWeight: '500' }}>{poolInfo.type}</div>
                      </td>
                      <td style={{
                        textAlign: 'center',
                        padding: '8px',
                        fontSize: '12px',
                        whiteSpace: 'nowrap',
                        color: poolInfo.location === 'On-Site' ? '#1565c0' : '#2e7d32',
                        fontWeight: '600'
                      }}>
                        {poolInfo.location}
                      </td>
                      <td style={{ textAlign: 'center', padding: '8px', fontSize: '14px', whiteSpace: 'nowrap', fontWeight: '600' }}>
                        {record.allocated_fte}
                      </td>
                      <td style={{ textAlign: 'center', padding: '8px', fontSize: '14px', whiteSpace: 'nowrap' }}>
                        <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', alignItems: 'center' }}>
                          <button
                            onClick={() => handleEdit(record)}
                            title="View allocation details"
                            style={{
                              padding: '4px 6px',
                              backgroundColor: 'transparent',
                              color: '#1976d2',
                              border: '1px solid #1976d2',
                              borderRadius: '4px',
                              fontSize: '14px',
                              cursor: 'pointer',
                              minWidth: 'auto'
                            }}
                          >
                            👁️
                          </button>
                          {!readOnly && (
                            <>
                              <button
                                onClick={() => handleEdit(record)}
                                title="Edit allocation"
                                style={{
                                  padding: '4px 6px',
                                  backgroundColor: 'transparent',
                                  color: '#1976d2',
                                  border: '1px solid #1976d2',
                                  borderRadius: '4px',
                                  fontSize: '14px',
                                  cursor: 'pointer',
                                  minWidth: 'auto'
                                }}
                              >
                                ✏️
                              </button>
                              <button
                                onClick={() => handleDelete(record.allocation_id)}
                                title="Delete allocation"
                                style={{
                                  padding: '4px 6px',
                                  backgroundColor: 'transparent',
                                  color: '#d32f2f',
                                  border: '1px solid #d32f2f',
                                  borderRadius: '4px',
                                  fontSize: '14px',
                                  cursor: 'pointer',
                                  minWidth: 'auto'
                                }}
                              >
                                🗑️
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Resource Allocation Form Modal */}
      {(showForm || showBulkForm) && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            maxWidth: '800px',
            width: '95%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
          }}>
            <div style={{
              padding: '24px',
              borderBottom: '1px solid #e0e0e0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{margin: 0, fontSize: '24px', fontWeight: '400', color: '#212121'}}>
                {showBulkForm ? 'Add New Resource Allocation' : viewOnlyMode ? 'View Resource Allocation (Read Only)' : editingRecord ? 'Edit Resource Allocation' : 'Add Single Phase Allocation'}
              </h2>
              <button 
                onClick={handleCloseForm}
                style={{
                  width: '32px',
                  height: '32px',
                  border: 'none',
                  background: 'transparent',
                  fontSize: '24px',
                  cursor: 'pointer',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#757575'
                }}
              >
                ×
              </button>
            </div>
            <div style={{padding: '24px'}}>
              {showBulkForm ? (
                // Bulk Phase Allocation Form
                <form onSubmit={handleBulkSubmit}>
                  {error && (
                    <div className="md-error" style={{ marginBottom: '16px' }}>
                      {error}
                    </div>
                  )}
                  
                  <div className="md-form-section" style={{marginBottom: '24px'}}>
                    <h3 style={{fontSize: '16px', margin: '0 0 16px 0', fontWeight: '500'}}>Application Information</h3>

                    {/* Grouping ID Dropdown */}
                    <div className="md-form-group" style={{marginBottom: '20px'}}>
                      <label style={{fontSize: '16px', fontWeight: '500', marginBottom: '8px', display: 'block'}}>Integration Group *</label>
                      <select
                        className="md-select"
                        style={{
                          width: '100%',
                          padding: '14px 16px',
                          fontSize: '16px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          backgroundColor: 'white',
                          cursor: 'pointer'
                        }}
                        value={bulkFormData.groupingId}
                        onChange={(e) => {
                          setBulkFormData({
                            ...bulkFormData,
                            groupingId: e.target.value,
                            applicationIdCode: '' // Reset application when group changes
                          });
                          setShowAppDropdown(false);
                        }}
                        required
                      >
                        <option value="">Select Integration Group</option>
                        {integrationGroups.map(group => (
                          <option key={group.integration_group_id} value={group.integration_group_id}>
                            {group.integration_group_id} - {group.integration_group_description} ({group.application_count} apps)
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Application ID Dropdown */}
                    <div className="md-form-group" style={{marginBottom: '20px'}}>
                      <label style={{fontSize: '16px', fontWeight: '500', marginBottom: '8px', display: 'block'}}>Application *</label>
                      <div className="custom-dropdown" style={{ position: 'relative' }}>
                        <div
                          onClick={() => {
                            if (bulkFormData.groupingId) {
                              setShowAppDropdown(!showAppDropdown);
                            }
                          }}
                          style={{
                            padding: '12px 16px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            backgroundColor: !bulkFormData.groupingId ? '#f5f5f5' : 'white',
                            cursor: bulkFormData.groupingId ? 'pointer' : 'not-allowed',
                            fontSize: '16px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}
                        >
                          <span style={{ color: bulkFormData.applicationIdCode ? '#212121' : '#9e9e9e', display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                            {bulkFormData.applicationIdCode && editableApplications.find(a => a.application_id === bulkFormData.applicationIdCode)
                              ? (
                                <>
                                  <span>{editableApplications.find(a => a.application_id === bulkFormData.applicationIdCode)!.application_id} - {editableApplications.find(a => a.application_id === bulkFormData.applicationIdCode)!.description || editableApplications.find(a => a.application_id === bulkFormData.applicationIdCode)!.application_name}</span>
                                  {editableApplications.find(a => a.application_id === bulkFormData.applicationIdCode)!.disposition && (
                                    <span style={{
                                      padding: '2px 8px',
                                      borderRadius: '10px',
                                      fontSize: '12px',
                                      fontWeight: '500',
                                      backgroundColor: getDispositionColor(editableApplications.find(a => a.application_id === bulkFormData.applicationIdCode)!.disposition).bg,
                                      color: getDispositionColor(editableApplications.find(a => a.application_id === bulkFormData.applicationIdCode)!.disposition).color
                                    }}>
                                      {editableApplications.find(a => a.application_id === bulkFormData.applicationIdCode)!.disposition}
                                    </span>
                                  )}
                                </>
                              )
                              : (bulkFormData.groupingId ? 'Select Application' : 'Select Integration Group first')}
                          </span>
                          {bulkFormData.groupingId && <span>▼</span>}
                        </div>
                        {showAppDropdown && bulkFormData.groupingId && (
                          <div style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            backgroundColor: 'white',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                            zIndex: 1000,
                            maxHeight: '200px',
                            overflowY: 'auto'
                          }}>
                            {editableApplications.length === 0 ? (
                              <div style={{ padding: '12px 16px', color: '#9e9e9e', fontSize: '16px' }}>
                                No editable applications available for this group
                              </div>
                            ) : (
                              editableApplications.map(app => (
                                <div
                                  key={app.application_id}
                                  onClick={() => {
                                    setBulkFormData({...bulkFormData, applicationIdCode: app.application_id});
                                    setShowAppDropdown(false);
                                  }}
                                  style={{
                                    padding: '12px 16px',
                                    cursor: 'pointer',
                                    fontSize: '16px',
                                    borderBottom: '1px solid #f0f0f0',
                                    backgroundColor: bulkFormData.applicationIdCode === app.application_id ? '#e3f2fd' : 'white',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center'
                                  }}
                                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = bulkFormData.applicationIdCode === app.application_id ? '#e3f2fd' : 'white')}
                                >
                                  <span><strong>{app.application_id}</strong> - {app.description || app.application_name}</span>
                                  {app.disposition && (
                                    <span style={{
                                      padding: '2px 8px',
                                      borderRadius: '10px',
                                      fontSize: '11px',
                                      fontWeight: '500',
                                      backgroundColor: getDispositionColor(app.disposition).bg,
                                      color: getDispositionColor(app.disposition).color,
                                      marginLeft: '8px'
                                    }}>
                                      {app.disposition}
                                    </span>
                                  )}
                                </div>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Resource Pool Dropdown */}
                    <div className="md-form-group" style={{marginBottom: '20px'}}>
                      <label style={{fontSize: '16px', fontWeight: '500', marginBottom: '8px', display: 'block'}}>Resource Pool *</label>
                      <select
                        className="md-select"
                        style={{
                          width: '100%',
                          padding: '14px 16px',
                          fontSize: '16px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          backgroundColor: 'white',
                          cursor: 'pointer'
                        }}
                        value={bulkFormData.resourcePoolId}
                        onChange={(e) => setBulkFormData({...bulkFormData, resourcePoolId: e.target.value})}
                        required
                      >
                        <option value="">Select Resource Pool</option>
                        {resourcePools.map(pool => (
                          <option key={pool.resource_pool_id} value={pool.resource_pool_id}>
                            {pool.resource_type} - {pool.resource_location}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="md-form-section">
                    <h3 style={{ fontSize: '16px', marginBottom: '16px' }}>FTE Allocation by Phase</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '12px' }}>
                      {STANDARD_PHASES.map(phase => (
                        <div key={phase} className="md-form-group" style={{ marginBottom: '8px' }}>
                          <label style={{ 
                            fontSize: '11px', 
                            fontWeight: '500', 
                            display: 'flex',
                            alignItems: 'flex-end',
                            marginBottom: '4px',
                            height: '28px',
                            lineHeight: '14px'
                          }}>
                            {phase.replace(/_/g, ' ')}
                          </label>
                          <input
                            type="number"
                            className="md-input no-spinners"
                            value={bulkFormData.allocations[phase] || ''}
                            onChange={(e) => setBulkFormData({
                              ...bulkFormData,
                              allocations: {
                                ...bulkFormData.allocations,
                                [phase]: e.target.value
                              }
                            })}
                            placeholder="0, 0.25, 1.0, 2.5"
                            min="0"
                            max="10"
                            step="0.01"
                            style={{
                              fontSize: '12px',
                              padding: '6px 8px',
                              height: '32px',
                              MozAppearance: 'textfield'
                            }}
                          />
                        </div>
                      ))}
                    </div>
                    
                    {/* Show allocation summary */}
                    {Object.keys(bulkFormData.allocations).some(phase => bulkFormData.allocations[phase] !== '') && (
                      <div style={{ 
                        marginTop: '12px', 
                        padding: '8px', 
                        backgroundColor: '#f8f9fa', 
                        borderRadius: '3px',
                        border: '1px solid #e9ecef'
                      }}>
                        <div style={{ fontSize: '11px', fontWeight: '500', marginBottom: '4px' }}>
                          Records to Create ({Object.entries(bulkFormData.allocations).filter(([_, fte]) => fte !== '' && !isNaN(parseFloat(fte))).length} individual):
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '2px' }}>
                          {Object.entries(bulkFormData.allocations)
                            .filter(([_, fte]) => fte !== '' && !isNaN(parseFloat(fte)))
                            .map(([phase, fte]) => (
                              <div key={phase} style={{ fontSize: '10px', color: '#666', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <span style={{ color: parseFloat(fte) === 0 ? '#ff9800' : '#4caf50' }}>
                                  {parseFloat(fte) === 0 ? '📝' : '📄'}
                                </span>
                                <span style={{ color: parseFloat(fte) === 0 ? '#f57f17' : '#333' }}>
                                  {phase.replace(/_/g, ' ')}: {fte} {parseFloat(fte) === 0 ? '(placeholder)' : ''}
                                </span>
                              </div>
                            ))
                          }
                        </div>
                        <div style={{ fontSize: '10px', color: '#28a745', fontWeight: '500', marginTop: '6px', padding: '4px', backgroundColor: '#f1f8e9', borderRadius: '2px' }}>
                          ✅ {Object.entries(bulkFormData.allocations).filter(([_, fte]) => fte !== '' && !isNaN(parseFloat(fte))).length} records • {Object.values(bulkFormData.allocations)
                            .filter(fte => fte !== '' && !isNaN(parseFloat(fte)))
                            .reduce((sum, fte) => sum + parseFloat(fte), 0)
                            .toFixed(1)} total FTE
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div style={{display: 'flex', justifyContent: 'flex-end', gap: '12px', paddingTop: '20px', borderTop: '1px solid #e0e0e0'}}>
                    <button
                      type="button"
                      onClick={handleCloseForm}
                      style={{padding: '12px 24px', fontSize: '16px', fontWeight: '500', border: '1px solid #1976d2', borderRadius: '4px', cursor: 'pointer', backgroundColor: 'transparent', color: '#1976d2'}}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      style={{padding: '12px 24px', fontSize: '16px', fontWeight: '500', border: 'none', borderRadius: '4px', cursor: 'pointer', backgroundColor: '#1976d2', color: 'white'}}
                    >
                      Create {Object.entries(bulkFormData.allocations).filter(([_, fte]) => fte && parseFloat(fte) > 0).length || 'Multi'} Records
                    </button>
                  </div>
                </form>
              ) : (
                // Single Allocation Form
                <form onSubmit={handleSubmit}>
                  {error && (
                    <div className="md-error" style={{ marginBottom: '16px' }}>
                      {error}
                    </div>
                  )}

                  <div className="md-form-section" style={{marginBottom: '24px'}}>
                    <h3 style={{fontSize: '16px', margin: '0 0 16px 0', fontWeight: '500'}}>Application Information</h3>

                    {/* Integration Group Dropdown */}
                    <div className="md-form-group" style={{marginBottom: '20px'}}>
                      <label style={{fontSize: '16px', fontWeight: '500', marginBottom: '8px', display: 'block'}}>Integration Group *</label>
                      <select
                        className="md-select"
                        style={{
                          width: '100%',
                          padding: '14px 16px',
                          fontSize: '16px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          backgroundColor: viewOnlyMode ? '#f5f5f5' : 'white',
                          cursor: viewOnlyMode ? 'not-allowed' : 'pointer'
                        }}
                        value={formData.groupingId}
                        onChange={(e) => {
                          setFormData({
                            ...formData,
                            groupingId: e.target.value,
                            applicationIdCode: '' // Reset application when group changes
                          });
                          setShowSingleAppDropdown(false);
                        }}
                        required
                        disabled={viewOnlyMode}
                      >
                        <option value="">Select Integration Group</option>
                        {integrationGroups.map(group => (
                          <option key={group.integration_group_id} value={group.integration_group_id}>
                            {group.integration_group_id} - {group.integration_group_description} ({group.application_count} apps)
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Application ID Dropdown */}
                    <div className="md-form-group" style={{marginBottom: '20px'}}>
                      <label style={{fontSize: '16px', fontWeight: '500', marginBottom: '8px', display: 'block'}}>Application *</label>
                      <div className="custom-dropdown" style={{ position: 'relative' }}>
                        <div
                          onClick={() => {
                            if (formData.groupingId && !viewOnlyMode) {
                              setShowSingleAppDropdown(!showSingleAppDropdown);
                            }
                          }}
                          style={{
                            padding: '12px 16px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            backgroundColor: (!formData.groupingId || viewOnlyMode) ? '#f5f5f5' : 'white',
                            cursor: (formData.groupingId && !viewOnlyMode) ? 'pointer' : 'not-allowed',
                            fontSize: '16px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}
                        >
                          <span style={{ color: formData.applicationIdCode ? '#212121' : '#9e9e9e', display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                            {formData.applicationIdCode && singleFormApplications.find(a => a.application_id === formData.applicationIdCode)
                              ? (
                                <>
                                  <span>{singleFormApplications.find(a => a.application_id === formData.applicationIdCode)!.application_id} - {singleFormApplications.find(a => a.application_id === formData.applicationIdCode)!.description || singleFormApplications.find(a => a.application_id === formData.applicationIdCode)!.application_name}</span>
                                  {singleFormApplications.find(a => a.application_id === formData.applicationIdCode)!.disposition && (
                                    <span style={{
                                      padding: '2px 8px',
                                      borderRadius: '10px',
                                      fontSize: '12px',
                                      fontWeight: '500',
                                      backgroundColor: getDispositionColor(singleFormApplications.find(a => a.application_id === formData.applicationIdCode)!.disposition).bg,
                                      color: getDispositionColor(singleFormApplications.find(a => a.application_id === formData.applicationIdCode)!.disposition).color
                                    }}>
                                      {singleFormApplications.find(a => a.application_id === formData.applicationIdCode)!.disposition}
                                    </span>
                                  )}
                                </>
                              )
                              : (formData.groupingId ? 'Select Application' : 'Select Integration Group first')}
                          </span>
                          {formData.groupingId && !viewOnlyMode && <span>▼</span>}
                        </div>
                        {showSingleAppDropdown && formData.groupingId && !viewOnlyMode && (
                          <div style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            backgroundColor: 'white',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                            zIndex: 1000,
                            maxHeight: '200px',
                            overflowY: 'auto'
                          }}>
                            {editableSingleFormApplications.length === 0 ? (
                              <div style={{ padding: '12px 16px', color: '#9e9e9e', fontSize: '16px' }}>
                                No editable applications available for this group
                              </div>
                            ) : (
                              editableSingleFormApplications.map(app => (
                                <div
                                  key={app.application_id}
                                  onClick={() => {
                                    setFormData({...formData, applicationIdCode: app.application_id});
                                    setShowSingleAppDropdown(false);
                                  }}
                                  style={{
                                    padding: '12px 16px',
                                    cursor: 'pointer',
                                    fontSize: '16px',
                                    borderBottom: '1px solid #f0f0f0',
                                    backgroundColor: formData.applicationIdCode === app.application_id ? '#e3f2fd' : 'white',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center'
                                  }}
                                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = formData.applicationIdCode === app.application_id ? '#e3f2fd' : 'white')}
                                >
                                  <span><strong>{app.application_id}</strong> - {app.description || app.application_name}</span>
                                  {app.disposition && (
                                    <span style={{
                                      padding: '2px 8px',
                                      borderRadius: '10px',
                                      fontSize: '11px',
                                      fontWeight: '500',
                                      backgroundColor: getDispositionColor(app.disposition).bg,
                                      color: getDispositionColor(app.disposition).color,
                                      marginLeft: '8px'
                                    }}>
                                      {app.disposition}
                                    </span>
                                  )}
                                </div>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Phase Name Dropdown */}
                    <div className="md-form-group" style={{marginBottom: '20px'}}>
                      <label style={{fontSize: '16px', fontWeight: '500', marginBottom: '8px', display: 'block'}}>Phase Name *</label>
                      <select
                        className="md-select"
                        style={{
                          width: '100%',
                          padding: '14px 16px',
                          fontSize: '16px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          backgroundColor: viewOnlyMode ? '#f5f5f5' : 'white',
                          cursor: viewOnlyMode ? 'not-allowed' : 'pointer'
                        }}
                        value={formData.phaseName}
                        onChange={(e) => setFormData({...formData, phaseName: e.target.value})}
                        required
                        disabled={viewOnlyMode}
                      >
                        <option value="">Select a phase...</option>
                        {STANDARD_PHASES.map(phase => (
                          <option key={phase} value={phase}>
                            {phase.replace(/_/g, ' ')}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Resource Pool Dropdown */}
                    <div className="md-form-group" style={{marginBottom: '20px'}}>
                      <label style={{fontSize: '16px', fontWeight: '500', marginBottom: '8px', display: 'block'}}>Resource Pool *</label>
                      <select
                        className="md-select"
                        style={{
                          width: '100%',
                          padding: '14px 16px',
                          fontSize: '16px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          backgroundColor: viewOnlyMode ? '#f5f5f5' : 'white',
                          cursor: viewOnlyMode ? 'not-allowed' : 'pointer'
                        }}
                        value={formData.resourcePoolId}
                        onChange={(e) => setFormData({...formData, resourcePoolId: e.target.value})}
                        required
                        disabled={viewOnlyMode}
                      >
                        <option value="">Select Resource Pool</option>
                        {resourcePools.map(pool => (
                          <option key={pool.resource_pool_id} value={pool.resource_pool_id}>
                            {pool.resource_type} - {pool.resource_location}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Allocated FTE */}
                    <div className="md-form-group" style={{marginBottom: '20px'}}>
                      <label style={{fontSize: '16px', fontWeight: '500', marginBottom: '8px', display: 'block'}}>Allocated FTE *</label>
                      <input
                        type="number"
                        className="md-input"
                        style={{
                          width: '100%',
                          padding: '14px 16px',
                          fontSize: '16px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          backgroundColor: viewOnlyMode ? '#f5f5f5' : 'white'
                        }}
                        value={formData.allocatedFte}
                        onChange={(e) => setFormData({...formData, allocatedFte: e.target.value})}
                        placeholder="Full-Time Equivalent (e.g., 0.25, 0.5, 1.0)"
                        min="0"
                        max="10"
                        step="0.01"
                        required
                        disabled={viewOnlyMode}
                      />
                    </div>
                  </div>

                  <div style={{display: 'flex', justifyContent: 'flex-end', gap: '12px', paddingTop: '20px', borderTop: '1px solid #e0e0e0'}}>
                    <button
                      type="button"
                      onClick={handleCloseForm}
                      style={{padding: '12px 24px', fontSize: '16px', fontWeight: '500', border: '1px solid #1976d2', borderRadius: '4px', cursor: 'pointer', backgroundColor: 'transparent', color: '#1976d2'}}
                    >
                      {viewOnlyMode ? 'Close' : 'Cancel'}
                    </button>
                    {!viewOnlyMode && (
                      <button
                        type="submit"
                        style={{padding: '12px 24px', fontSize: '16px', fontWeight: '500', border: 'none', borderRadius: '4px', cursor: 'pointer', backgroundColor: '#1976d2', color: 'white'}}
                      >
                        {editingRecord ? 'Update Resource Allocation' : 'Create Resource Allocation'}
                      </button>
                    )}
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            maxWidth: '800px',
            width: '95%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
          }}>
            <div style={{
              padding: '24px',
              borderBottom: '1px solid #e0e0e0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{margin: 0, fontSize: '20px', fontWeight: '400'}}>Preview Import Data</h2>
              <button 
                onClick={() => setShowUploadModal(false)}
                style={{
                  width: '32px',
                  height: '32px',
                  border: 'none',
                  background: 'transparent',
                  fontSize: '24px',
                  cursor: 'pointer',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
            </div>
            <div style={{padding: '24px'}}>
              {previewData.length > 0 ? (
                <div>
                  <p>Found {previewData.length} records. Preview:</p>
                  <div style={{ maxHeight: '300px', overflow: 'auto', border: '1px solid #ddd', borderRadius: '4px' }}>
                    <table style={{ width: '100%', fontSize: '12px' }}>
                      <thead style={{ backgroundColor: '#f5f5f5' }}>
                        <tr>
                          {Object.keys(previewData[0] || {}).map(key => (
                            <th key={key} style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>
                              {key}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {previewData.slice(0, 5).map((row, index) => (
                          <tr key={index}>
                            {Object.values(row).map((value: any, cellIndex) => (
                              <td key={cellIndex} style={{ padding: '8px', borderBottom: '1px solid #eee' }}>
                                {value}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {previewData.length > 5 && (
                    <p style={{ fontSize: '12px', color: '#666' }}>
                      ... and {previewData.length - 5} more records
                    </p>
                  )}
                </div>
              ) : (
                <p>No data found in the uploaded file.</p>
              )}
              
              <div style={{display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px'}}>
                <button
                  onClick={() => setShowUploadModal(false)}
                  style={{padding: '12px 24px', fontSize: '14px', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer', backgroundColor: 'white'}}
                >
                  Cancel
                </button>
                {previewData.length > 0 && (
                  <button
                    onClick={handleBulkImport}
                    style={{padding: '12px 24px', fontSize: '14px', border: 'none', borderRadius: '4px', cursor: 'pointer', backgroundColor: '#1976d2', color: 'white'}}
                  >
                    Import {previewData.length} Records
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Success/Error Messages */}
      {success && (
        <div className="md-toast md-toast--success">
          {success}
          <button onClick={() => setSuccess('')}>×</button>
        </div>
      )}
      {error && (
        <div className="md-toast md-toast--error">
          {error}
          <button onClick={() => setError('')}>×</button>
        </div>
      )}
      </>
      )}
      </div>
    </div>
  );
};

export default ResourceAllocation;